import {
  IAttribute,
  IConfiguration,
  IDictionary,
  ILabelOptionValue,
  IOption,
  ITemplate,
} from "../../typings";
import EventController from "./event-controller";
import { IStorageController } from "./storage/istorage-controller";
import TemplateController from "./template-controller";
import ExternalController from "./external-controller";
import axios, { AxiosResponse } from "axios";
import { suportedFilesWithoutConversion } from "../configuration";
import IEventController from "./ievent-controller";

export default class OutputController {
  output: ITemplate;
  eventController: IEventController;
  templateController: TemplateController;
  storageController: IStorageController;
  private transacting: boolean = false;
  private sendChangeEventQueue: [Array<string>, any, any][] = [];
  private sendSaveEventQueue: [string, Array<string>][] = [];
  private externalController: ExternalController;
  private conf: IConfiguration;

  constructor(
    eventController: IEventController,
    templateController: TemplateController,
    storageController: IStorageController,
    externalController: ExternalController,
    conf: IConfiguration,
  ) {
    this.eventController = eventController;
    this.templateController = templateController;
    this.storageController = storageController;
    this.externalController = externalController;
    this.conf = conf;
  }

  setOutputTemplate(template: ITemplate): void {
    const output: ITemplate = template;
    this.output = output;
  }

  stateOutputTemplate(): boolean {
    return this.storageController.hasOutputTemplate();
  }

  getOutputTemplate(): ITemplate {
    if (this.output) {
      return this.output;
    }
    return this.storageController.getOutputTemplate();
  }

  saveOutputTemplate(output: ITemplate): void {
    try {
      delete output.parsed_options;
      delete output.options;
      delete output.meta;
    } catch (e) {}

    this.storageController.saveOutputTemplate(output);
    this.output = output;
  }

  importOutputTemplate(output: ITemplate): Promise<void> {
    if (!output.preview) {
      this.saveOutputTemplate(output);
      return Promise.resolve();
    }

    if (!output.values.original) {
      output.values.original = [output.preview];
    }

    const promises: Promise<void>[] = [];

    if (
      output.values["file-front"] &&
      output.values["file-front"][0] === "upload"
    ) {
      promises.push(
        this.importOutputTemplateFrom(
          output,
          "original",
          "original-meta",
          "original-png",
          "cropped-data",
        ),
      );
    }

    if (
      output.values["file-back"] &&
      output.values["file-back"][0] === "upload-backside" &&
      output.values["original-back"] &&
      output.values["original-back"][0]
    ) {
      promises.push(
        this.importOutputTemplateFrom(
          output,
          "original-back",
          "original-back-meta",
          "original-back-png",
          "cropped-data-back",
        ),
      );
    }

    return Promise.all(promises).then(() => {
      this.saveOutputTemplate(output);
    });
  }

  importOutputTemplateFrom(
    output: ITemplate,
    originalKey: string,
    originalMetaKey: string,
    originalPngKey: string,
    croppedDataKey: string,
  ): Promise<void> {
    OutputController.transformImageMetaData(output, originalKey);

    if (!output.values["unit-system"]) {
      output.values["unit-system"] = [this.conf.unitSystem];
    }

    if (!output.values["decimal-character"]) {
      output.values["decimal-character"] = [
        this.conf.decimalCharacter === "." ? "dot" : "colon",
      ];
    }

    const promises: Promise<void>[] = [];
    if (
      !output.values[croppedDataKey] &&
      output.values[originalPngKey] &&
      output.values[originalPngKey][0]
    ) {
      promises.push(
        this.generateCroppedData(output, croppedDataKey, originalPngKey),
      );
    }

    if (output.values[originalKey][0].slice(0, 5) === "data:") {
      promises.push(
        new Promise((resolve: Function) => {
          this.externalController
            .uploadImage(output.values[originalKey][0])
            .then((url: string) => {
              const [_, type]: RegExpMatchArray =
                OutputController.extractMimeTypeFromBase64(
                  output.values[originalKey][0],
                );
              output.values[originalKey] = [url];
              output.values[originalMetaKey] = [
                {
                  type,
                },
              ];
              resolve();
            });
        }),
      );
    } else if (
      !output.values[originalMetaKey] &&
      output.values[originalKey][0].slice(0, 4) === "http"
    ) {
      promises.push(
        new Promise((resolve: Function) => {
          axios
            .get(output.values[originalKey][0])
            .then((response: AxiosResponse<any>) => {
              output.values[originalMetaKey] = [
                {
                  type: response.headers["content-type"],
                },
              ];
              if (
                suportedFilesWithoutConversion.indexOf(
                  response.headers["content-type"],
                ) !== -1
              ) {
                output.values[originalPngKey] = output.values[originalKey];
                this.generateCroppedData(
                  output,
                  croppedDataKey,
                  originalPngKey,
                ).then(() => resolve());
              } else if (!output.values[originalPngKey]) {
                this.externalController
                  .convertImage(output.values[originalKey][0])
                  .then((url: string) => {
                    output.values[originalPngKey] = [url];
                    if (!output.values[croppedDataKey]) {
                      this.generateCroppedData(
                        output,
                        croppedDataKey,
                        originalPngKey,
                      ).then(() => resolve());
                    } else {
                      resolve();
                    }
                  });
              } else {
                resolve();
              }
            });
        }),
      );
    }

    return new Promise<void>((resolve: () => void) => {
      Promise.all(promises).then(resolve);
    });
  }

  getValue(name: Array<string>, id: string = null): any {
    if (id !== null) {
      if (id !== "") {
        name.push(id);
      }
    }

    if (this.output) {
      if (this.output.values) {
        if (name) {
          const part: any = this.output.values as any;
          if (part) {
            if (name[name.length - 1]) {
              const option: IOption = this.templateController.getOptionByCode(
                name[name.length - 1],
              );
              if (option) {
                if (option.attributes && option.attributes.fixed) {
                  return option.attributes.fixed.value;
                }
                if (
                  part[name[name.length - 1]] ||
                  part[name[name.length - 1]] === 0
                ) {
                  if (
                    option.attributes &&
                    option.attributes.multiple &&
                    option.attributes.multiple.value === false
                  ) {
                    return part[name[name.length - 1]][0];
                  }
                  return part[name[name.length - 1]];
                }
              }
            }
          }
        }
      }
    }
    return null;
  }

  getIsMultipleAttributebyCode(code: string): boolean | null {
    const option: IOption = this.templateController.getOptionByCode(code);
    return option?.attributes?.multiple?.value;
  }

  setValue(
    name: Array<string>,
    value: any,
    defaultValue: boolean = false,
    id: string = null,
  ): Promise<void> {
    return new Promise((resolve: Function, reject: Function) => {
      // No name, error
      if (name === null) {
        return reject();
      }

      const option: IOption = this.templateController.getOptionByCode(
        name[name.length - 1],
      );

      if (option && option.attributes && option.attributes.fixed) {
        return resolve();
      }

      const oldValue: string = this.getValue(name);
      let save: boolean = false;

      if (typeof oldValue !== "undefined" && oldValue !== null) {
        //Check if value has been changed. Or doesnt exist
        if (!this.compareVariables(oldValue, value) && defaultValue === false) {
          save = true;
        }

        if (value instanceof Array) {
          value = value[0];
        }
      } else {
        save = true;

        if (value instanceof Array) {
          value = value[0];
        }
      }

      if (false === save) {
        return resolve();
      }

      if (!this.output) {
        return resolve();
      }

      if (this.output.uploader) {
        if (Array.isArray(this.output.uploader.design) === true) {
          this.output.uploader.design = {} as any;
        }
        this.output.uploader.design = this.saveValue(
          this.output.uploader.design,
          name,
          value,
        );
      }

      this.saveOutputValues(name, value);

      const eventName: string = EventController.getSaveEventName(name);
      this.sendSaveEvent(eventName, name);

      this.saveOutputTemplate(this.output);
      if (defaultValue === false) {
        this.sendChangeEvent(name, value, oldValue);
      }

      const triggerChangeOptionCodes: string[] =
        this.templateController.getDontTriggerChangeOptionCodes();
      if ([...triggerChangeOptionCodes, "preview"].indexOf(name[1]) !== -1) {
        return resolve();
      }

      const resetOptionCodes: string[] =
        this.templateController.getResetOptionCodes();
      resetOptionCodes.forEach((code: string) => {
        if (this.getValue(["image", code])) {
          this.setValue(["image", code], null);
        }
      });

      return resolve();
    });
  }

  private saveValue(output: any, name: Array<string>, value: any): any {
    if (name) {
      if (name.length <= 0) {
        return output;
      }
      const key: any = name[0];
      if (!output) {
        output = {};
      }

      if (key) {
        if (name.length === 1) {
          output[key] = value;
          return output;
        }
        if (!output[key]) {
          output[key] = {};
        }
        const temp: any = [...name];
        temp.shift();
        const keyObject: any = output[key];
        output[key] = this.saveValue(keyObject, temp, value);
        return output;
      }
    }
    return output;
  }

  private saveOutputValues(name: Array<string>, value: any): void {
    if (!this.output.values) {
      this.output.values = {};
    }
    if (Array.isArray(this.output.values) === true) {
      this.output.values = {};
    }

    if (name[1] === "preview") {
      this.output.preview = value;
      return;
    }

    if (Array.isArray(value) === false) {
      value = [value];
    }

    this.output.values[name[1]] = value;
  }

  static transformImageMetaData(output: ITemplate, key: string): void {
    if (output.values[key + "-meta"]) {
      return;
    }
    if (!output.values[key]) {
      return;
    }
    const value: string = output.values[key][0];
    if (value.slice(0, 5) === "data:") {
      const type: string = value.match(
        /data:([a-zA-Z0-9]+\/[a-zA-Z0-9-.+]+).*,.*/,
      )[1];
      if (!type) {
        return;
      }
      output.values[key + "-meta"] = [{ type }];
    }
    return;
  }

  startEventTransaction(): void {
    if (this.transacting) {
      throw new Error("There is already a transaction running!");
    }

    this.transacting = true;
  }

  commitEventTransaction(): void {
    this.transacting = false;
    this.sendChangeEventQueue.forEach(
      ([type, value, oldValue]: [Array<string>, any, any]) => {
        this.externalController._triggerOnChangeEvents(type, value, oldValue);
      },
    );
    this.sendChangeEventQueue = [];

    this.sendSaveEventQueue.forEach(
      ([eventName, name]: [string, Array<string>]) => {
        this.eventController.sendSaveEvent(eventName, name);
      },
    );
    this.sendSaveEventQueue = [];
  }

  private sendChangeEvent(
    type: Array<string>,
    value: any,
    oldValue: any,
  ): void {
    if (this.transacting) {
      this.sendChangeEventQueue.push([type, value, oldValue]);
      return;
    }
    this.externalController._triggerOnChangeEvents(type, value, oldValue);
  }

  private sendSaveEvent(eventName: string, name: Array<string>): void {
    if (this.transacting) {
      this.sendSaveEventQueue.push([eventName, name]);
      return;
    }
    this.eventController.sendSaveEvent(eventName, name);
  }

  private compareVariables(a: any, b: any): boolean {
    if (typeof a === "boolean" && typeof b === "boolean") {
      return a === b;
    }

    if (typeof a === "string" && typeof b === "string") {
      return a === b;
    }

    if (typeof a === "number" && typeof b === "number") {
      return a === b;
    }

    if (Array.isArray(a) && Array.isArray(b)) {
      return a.every((currentValue: any, index: number) =>
        this.compareVariables(currentValue, b[index]),
      );
    }

    if (typeof a === "object" && typeof b === "object") {
      return (
        Object.keys(a).every((currentIndex: string) =>
          this.compareVariables(a[currentIndex], b[currentIndex]),
        ) && Object.keys(a).length === Object.keys(b).length
      );
    }

    return false;
  }

  processDefaults(): void {
    const output: ITemplate = Object.assign({}, this.output);
    const options: IDictionary<IOption> =
      this.templateController.getTemplate().options;

    if (!output.values) {
      output.values = {};
    }

    if (!output.values["unit-system"]) {
      output.values["unit-system"] = [this.conf.unitSystem];
    }

    Object.keys(options).forEach((key: string) => {
      if (output.values[key]) {
        return;
      }

      if (typeof options[key].default === "undefined") {
        return;
      }

      if (options[key].default === null) {
        return;
      }

      if (options[key].default === "") {
        return;
      }

      this.setValue(["", key], [options[key].default])
        .then(() => {})
        .catch(() => {});
    });
  }

  private static extractMimeTypeFromBase64(value: string): RegExpMatchArray {
    if (typeof value !== "string") {
      return null;
    }
    return value.match(/data:([a-zA-Z0-9]+\/[a-zA-Z0-9-.+]+).*,.*/);
  }

  private generateCroppedData(
    output: ITemplate,
    croppedData: string,
    originalPng: string,
  ): Promise<void> {
    return new Promise((resolve: Function) => {
      const image: HTMLImageElement = new Image();
      image.addEventListener("load", () => {
        const fixedWidth: IAttribute =
          this.templateController.getOptionByCode("width").attributes.fixed;
        const fixedHeight: IAttribute =
          this.templateController.getOptionByCode("height").attributes.fixed;
        const outputWidth: number = output.values.width
          ? output.values.width[0]
          : fixedWidth
            ? fixedWidth.value
            : 70;
        const outputHeight: number = output.values.height
          ? output.values.height[0]
          : fixedHeight
            ? fixedHeight.value
            : 70;
        let width: number = image.width;
        let height: number = image.width * (outputHeight / outputWidth);
        let x: number = 0;
        let y: number = (image.height - height) / 2;

        if (height > image.height) {
          width = image.height * (outputWidth / outputHeight);
          height = image.height;
          x = (image.width - width) / 2;
          y = 0;
        }

        output.values[croppedData] = [
          {
            width,
            height,
            x,
            y,
            rotate: 0,
            scaleX: 1,
            scaleY: 1,
          },
        ];
        resolve();
      });
      image.crossOrigin = "anonymous";
      image.src = output.values[originalPng][0];
    });
  }

  private conditions: IDictionary<(filter: any) => boolean> = {
    eq(filter: { option: string; value: string }): boolean {
      const value: string = this.getValue(["image", filter.option]);
      return value === filter.value;
    },
    neq(filter: { option: string; value: string }): boolean {
      const value: string = this.getValue(["image", filter.option]);
      return value !== filter.value;
    },
    and({ value }: { value: [] }): boolean {
      return value.reduce(
        (carry: boolean, filter: { type: string }) =>
          carry && this.conditions[filter.type].bind(this)(filter),
        true,
      );
    },
    or({ value }: { value: [] }): boolean {
      return value.reduce(
        (carry: boolean, filter: { type: string }) =>
          carry || this.conditions[filter.type].bind(this)(filter),
        false,
      );
    },
  };

  optionAttributeCondition(attribute: IAttribute): boolean {
    return this.conditions.and.bind(this)(attribute);
  }

  getOptionAttributeBoolean(optionKey: string, attributeKey: string): boolean {
    const template: ITemplate = this.templateController.getTemplate();
    const option: IOption = template.options[optionKey];
    return this.getAttributeBoolean(option, attributeKey);
  }

  getAttributeBoolean(
    option: IOption | ILabelOptionValue,
    key: string,
  ): boolean {
    if (!option || !option.attributes) {
      return false;
    }

    const attribute: IAttribute = option.attributes[key];
    if (!attribute) {
      return false;
    }

    if (Array.isArray(attribute.value)) {
      return this.optionAttributeCondition(attribute);
    }

    return typeof attribute.value === "boolean" ? attribute.value : false;
  }

  getOptionFilteredValues(option: IOption): IOption {
    const filteredOption: IOption = {
      ...option,
      values: option.values.filter(
        (value: ILabelOptionValue) => !this.getAttributeBoolean(value, "hide"),
      ),
    };

    const value: any = this.getValue(["label", option.code]);
    if (
      value &&
      !filteredOption.attributes?.multiple?.value &&
      filteredOption.values.length > 0 &&
      !filteredOption.values.find(
        (optionValue: ILabelOptionValue) => optionValue.value === value,
      )
    ) {
      this.setValue(["label", option.code], option.default)
        .then(() => {})
        .catch(() => {});
    }

    return filteredOption;
  }
}
